import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../../store/gameStore';
import { useTranslation } from '../../hooks/useTranslation';
import { BuildingModal } from './BuildingModal';
import { getFighterSpritePath, Fighter, getFighterAttack, getFighterPassiveDefense, getFighterMaxHealth, getFighterMaxStamina } from '../../types/fighter';
import { getDepthEnemyInfo, getDepthLoot } from '../../utils/raidEnemies';

interface RaidModalProps {
  onClose: () => void;
}

// Генерируем информацию о глубинах автоматически
const DEPTH_INFO = [1, 2, 3, 4, 5].map(depth => ({
  ...getDepthEnemyInfo(depth),
  loot: getDepthLoot(depth),
}));

export const RaidModal: React.FC<RaidModalProps> = ({ onClose }) => {
  const t = useTranslation();
  const { fighters, raidStats, getAttackCooldown } = useGameStore();
  const [selectedFighters, setSelectedFighters] = useState<string[]>([]);
  const [selectedDepth, setSelectedDepth] = useState<number>(1);
  const [showHelp, setShowHelp] = useState<number | null>(null);
  const navigate = useNavigate();

  // Проверяем, готово ли нападение орков (таймер закончился)
  const attackCooldown = getAttackCooldown();
  const isAttackReady = attackCooldown === 0;

  // Получаем возможных врагов для глубины (используем общую утилиту)
  const getEnemyTypesForDepth = (depth: number): Fighter[] => {
    return getDepthEnemyInfo(depth).possibleEnemies;
  };

  const toggleFighter = (fighterId: string) => {
    if (selectedFighters.includes(fighterId)) {
      setSelectedFighters(selectedFighters.filter(id => id !== fighterId));
    } else if (selectedFighters.length < 5) {
      setSelectedFighters([...selectedFighters, fighterId]);
    }
  };

  const handleStartRaid = () => {
    if (selectedFighters.length === 0) return;



    const raidFighters = fighters.filter(f => selectedFighters.includes(f.id));

    navigate('/battle', {
      state: {
        fighters: raidFighters,
        isRaid: true,
        depth: selectedDepth,
        enemyCount: selectedDepth * 2 - 1,
      }
    });
  };

  return (
    <>
      <BuildingModal
        title={t.modals.raid.title}
        onClose={onClose}
        data-tutorial="raidModal"
        content={
          <div className="text-white font-pixel text-xs flex flex-col md:flex-row gap-3 md:gap-4 max-h-[85vh] md:max-h-[70vh] overflow-x-hidden">
            {/* ЛЕВАЯ ПАНЕЛЬ: Блок-схема глубин */}
            <div className="w-full md:w-80 md:flex-shrink-0 overflow-y-auto pr-2 custom-scrollbar max-h-[40vh] md:max-h-none">
              <div className="text-yellow-600 mb-3 text-xs md:text-[10px] uppercase tracking-widest">{t.modals.raid.selectDepth}</div>
              <div className="relative">
                {DEPTH_INFO.map((info, index) => (
                  <div key={info.depth} className="relative">
                    {/* Блок глубины */}
                    <button
                      data-tutorial={`raidDepth${info.depth}`}
                      data-selected={selectedDepth === info.depth ? 'true' : 'false'}
                      onClick={() => setSelectedDepth(info.depth)}
                      className={`w-full p-3 border-2 rounded transition-all relative ${selectedDepth === info.depth
                        ? 'border-yellow-500 bg-yellow-500/20 shadow-[0_0_15px_rgba(234,179,8,0.4)]'
                        : 'border-gray-700 bg-black/40 hover:border-gray-600'
                        }`}
                    >
                      {/* Заголовок */}
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <div className="text-yellow-500 text-[10px] font-bold">{t.modals.raid.depth} {info.depth}</div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowHelp(showHelp === info.depth ? null : info.depth);
                            }}
                            className="text-[8px] px-1.5 py-0.5 bg-blue-700 hover:bg-blue-600 border border-blue-500 rounded"
                            title={t.modals.raid.help}
                          >
                            ?
                          </button>
                        </div>
                        {raidStats[info.depth] > 0 && (
                          <div className="bg-green-700 border border-green-500 text-[7px] px-1.5 py-0.5 rounded">
                            ✓ {raidStats[info.depth]}
                          </div>
                        )}
                      </div>

                      {/* Враги - показываем спрайты орков (все возможные типы) */}
                      <div className="mb-2">
                        <div className="text-[7px] text-gray-400 mb-1 uppercase">{t.modals.raid.enemies} ({info.enemyCount}):</div>
                        <div className="flex gap-1 flex-wrap">
                          {(() => {
                            // Используем возможных врагов из информации о глубине
                            const uniqueEnemies = Array.from(
                              new Map(info.possibleEnemies.map(e => [e.name, e])).values()
                            );
                            return uniqueEnemies.map((enemy, i) => (
                              <div key={i} className="w-10 h-10 bg-black/60 border border-red-900/50 rounded flex items-center justify-center overflow-hidden">
                                <img
                                  src={getFighterSpritePath(enemy)}
                                  alt={enemy.name}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    console.error('Failed to load sprite for:', enemy.name, getFighterSpritePath(enemy));
                                    (e.target as any).style.display = 'none';
                                    const el = (e.target as any).parentElement;
                                    if (!el.getAttribute('data-has-emoji')) {
                                      el.setAttribute('data-has-emoji', 'true');
                                      el.insertAdjacentHTML('beforeend', '<span class="text-xs">👹</span>');
                                    }
                                  }}
                                />
                              </div>
                            ));
                          })()}
                        </div>
                      </div>

                      {/* Лут */}
                      <div className="bg-black/40 border border-white/5 p-1.5 rounded">
                        <div className="text-[7px] text-gray-400 mb-1 uppercase">{t.modals.raid.reward}</div>
                        <div className="flex flex-wrap gap-2 text-[7px]">
                          <span className="text-yellow-500">🪙 {info.loot.gold}</span>
                          <span className="text-gray-300">⚒️ {info.loot.metal}</span>
                          <span className="text-orange-400">🌲 {info.loot.wood}</span>
                          <span className="text-amber-600">🦌 {info.loot.leather || 0}</span>
                          {info.loot.crystals > 0 && <span className="text-blue-400">💎 {info.loot.crystals}</span>}
                        </div>
                      </div>
                    </button>

                    {/* Соединительная линия */}
                    {index < DEPTH_INFO.length - 1 && (
                      <div className="flex justify-center my-1">
                        <div className="w-0.5 h-4 bg-gradient-to-b from-gray-600 to-gray-700"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ПРАВАЯ ПАНЕЛЬ: Выбор бойцов */}
            <div className="w-full md:w-64 md:flex-shrink-0 flex flex-col">
              <div className="text-yellow-600 mb-2 text-xs md:text-[10px] uppercase tracking-widest">{t.modals.raid.selectFighters} ({t.modals.raid.maxFighters}):</div>
              <div data-tutorial="selectFighters" className="flex-1 space-y-2 overflow-y-auto custom-scrollbar max-h-[40vh] md:max-h-none">
                {fighters.map((fighter) => {
                  const isSelected = selectedFighters.includes(fighter.id);
                  const rankName = t.getFighterRankName(fighter.rank);
                  return (
                    <button
                      key={fighter.id}
                      onClick={() => toggleFighter(fighter.id)}
                      disabled={!isSelected && selectedFighters.length >= 5}
                      className={`w-full p-2 md:p-2 text-left border-2 rounded transition-all ${isSelected
                        ? 'border-yellow-500 bg-yellow-500/20'
                        : 'border-gray-700 bg-black/20 hover:border-gray-600 disabled:opacity-30 disabled:cursor-not-allowed'
                        }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <img
                          src={getFighterSpritePath(fighter)}
                          alt={fighter.name}
                          className="w-10 h-10 md:w-8 md:h-8 object-contain flex-shrink-0"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm md:text-xs font-bold truncate">{fighter.name}</div>
                          <div className="text-xs md:text-[10px] text-yellow-500">{rankName}</div>
                          {fighter.rank < 2 && (
                            <div className="text-[9px] md:text-[8px] text-white/40 uppercase mt-0.5">
                              {t.modals.raid.progress} {fighter.rankPoints}/{fighter.rank === 0 ? 3 : 9}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 md:gap-2 text-[10px] md:text-[9px] text-white/60 mb-1">
                        <span className="break-words">{fighter.leftHand ? t.getWeaponName(fighter.leftHand.name) : '—'}</span>
                        {fighter.armor && <><span className="opacity-30">|</span> <span className="break-words">{t.getWeaponName(fighter.armor.name)}</span></>}
                        {fighter.rightHand && <><span className="opacity-30">|</span> <span className="break-words">{t.getWeaponName(fighter.rightHand.name)}</span></>}
                      </div>
                      <div className="flex justify-between items-center text-xs md:text-[10px]">
                        <span className="text-red-400">❤️ {fighter.health}/{fighter.maxHealth}</span>
                        <span className="text-yellow-400">⚡ {fighter.stamina}/{fighter.maxStamina}</span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Кнопка старта */}
              <div className="mt-4 pt-3 border-t border-white/10">
                {isAttackReady && (
                  <div className="mb-2 text-red-500 text-[9px] text-center font-bold animate-pulse uppercase border border-red-500/50 bg-red-900/20 p-2 rounded">
                    ⚠️ {t.modals.raid.attackBlocked}
                  </div>
                )}
                <div className="mb-2 text-gray-400 text-[8px] text-center">
                  {t.modals.raid.selected} {selectedFighters.length}/5
                </div>
                <button
                  onClick={handleStartRaid}
                  disabled={selectedFighters.length === 0 || isAttackReady}
                  className="w-full px-4 py-3 bg-red-700 hover:bg-red-600 disabled:bg-gray-800 disabled:cursor-not-allowed border-2 border-red-500 disabled:border-gray-700 text-[10px] font-bold uppercase tracking-wider transition-all shadow-[0_4px_0_0_rgba(127,29,29,1)] active:shadow-none active:translate-y-1"
                >
                  {t.modals.raid.startRaid}!
                </button>
              </div>
            </div>
          </div>
        }
      />
      {showHelp !== null && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 font-pixel" onClick={() => setShowHelp(null)}>
          <div className="bg-black/95 border-4 border-yellow-500 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl text-yellow-500 uppercase">{t.modals.raid.helpTitle.replace('{depth}', String(showHelp))}</h2>
              <button
                onClick={() => setShowHelp(null)}
                className="text-white hover:text-red-400 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <div className="text-white text-xs">
              <div className="mb-4 text-gray-400">{t.modals.raid.helpDescription}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {getEnemyTypesForDepth(showHelp).map((enemy, i) => (
                  <div key={i} className="bg-black/60 border border-white/10 p-3 rounded flex gap-3">
                    <div className="w-16 h-16 bg-black/80 border border-white/20 rounded flex items-center justify-center flex-shrink-0">
                      <img
                        src={getFighterSpritePath(enemy)}
                        alt={enemy.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as any).style.display = 'none';
                          const el = (e.target as any).parentElement;
                          if (!el.getAttribute('data-has-emoji')) {
                            el.setAttribute('data-has-emoji', 'true');
                            el.insertAdjacentHTML('beforeend', '<span class="text-2xl">👹</span>');
                          }
                        }}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-yellow-400 font-bold mb-1 text-sm">{t.getEnemyName(enemy.name)}</div>
                      <div className="text-[9px] text-gray-300 space-y-0.5">
                        <div>❤️ {t.modals.raid.health}: {getFighterMaxHealth(enemy)}</div>
                        <div>⚡ {t.modals.raid.stamina} {getFighterMaxStamina(enemy)}</div>
                        <div>⚔️ {t.modals.raid.attack} {getFighterAttack(enemy)}</div>
                        <div>🛡️ {t.modals.raid.defense} {getFighterPassiveDefense(enemy)} ({t.modals.raid.passive})</div>
                        {enemy.leftHand && <div>{t.modals.raid.weapons} {t.getWeaponName(enemy.leftHand.name)}</div>}
                        {enemy.rightHand && <div>{t.modals.raid.shields} {t.getWeaponName(enemy.rightHand.name)}</div>}
                        {enemy.armor && <div>{t.modals.raid.armor} {t.getWeaponName(enemy.armor.name)}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

